/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  ArrayPath,
  FieldArray,
  FieldValues,
  useFieldArray,
  UseFieldArrayAppend,
  UseFieldArrayProps,
} from 'react-hook-form';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DraggableAttributes,
  KeyboardSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import {
  AppendableFieldProps,
  DefaultAppendButton,
} from './AppendableField';

function DraggableIcon() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 4C3.10457 4 4 3.10457 4 2C4 0.89543 3.10457 0 2 0C0.89543 0 0 0.89543 0 2C0 3.10457 0.89543 4 2 4Z" fill="#CED4DA" />
      <path d="M2 10C3.10457 10 4 9.10457 4 8C4 6.89543 3.10457 6 2 6C0.89543 6 0 6.89543 0 8C0 9.10457 0.89543 10 2 10Z" fill="#CED4DA" />
      <path d="M2 16C3.10457 16 4 15.1046 4 14C4 12.8954 3.10457 12 2 12C0.89543 12 0 12.8954 0 14C0 15.1046 0.89543 16 2 16Z" fill="#CED4DA" />
      <path d="M8 4C9.10457 4 10 3.10457 10 2C10 0.89543 9.10457 0 8 0C6.89543 0 6 0.89543 6 2C6 3.10457 6.89543 4 8 4Z" fill="#CED4DA" />
      <path d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" fill="#CED4DA" />
      <path d="M8 16C9.10457 16 10 15.1046 10 14C10 12.8954 9.10457 12 8 12C6.89543 12 6 12.8954 6 14C6 15.1046 6.89543 16 8 16Z" fill="#CED4DA" />
    </svg>

  );
}

function DraggableButton(
  { attributes, listeners }:
  { attributes: DraggableAttributes, listeners?: SyntheticListenerMap }) {
  return (
    <button
      {...attributes}
      {...listeners}
      className="btn btn-gray-300 p-3"
          // Since is a very particular case
          // Using style is fine
      style={{ cursor: 'grab' }}
      type="button"
    >
      <DraggableIcon />
    </button>
  );
}

function SortableItem(
  {
    id,
    renderInput,
    removeButton,
  }
  : {
    id: string,
    renderInput: (isDragging: boolean) => JSX.Element,
    removeButton: JSX.Element | null,
  }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
  };
  return (
    <div ref={setNodeRef} style={style} className="row g-0">
      <div className="col-11">
        {renderInput(isDragging)}
      </div>
      <div className="col-1 d-flex flex-row align-items-center">
        {removeButton}
        <DraggableButton attributes={attributes} listeners={listeners} />
      </div>
    </div>
  );
}

interface DraggableAppendableFieldProps<T extends FieldValues> extends Omit<AppendableFieldProps<T>, 'renderInput'> {
  renderInput: (fieldId: string, index: number, isDragging: boolean) => JSX.Element,
  appendButtonPosition?: 'left' | 'right',
  labels?: {
    title: string,
    span: `col-${number}`,
  }[],
  maxItems?: number,
  rules?: UseFieldArrayProps<T>['rules'],
  control: UseFieldArrayProps<T>['control'],
  appendDefaultValues?: FieldArray<T, ArrayPath<T>>,
}

export function DraggableAppendableFieldLabels({ labels }: { labels: { title: string, span: `col-${number}` }[] }) {
  return (
    <div className="row g-3 mb-2">
      <div className="col-11">
        {labels && labels.map((label) => (
          <label key={`${label.title}-label`} className={`fs-sm fw-bold text-primary ${label.span}`} htmlFor={`${label.title}`}>{label.title}</label>
        ))}
      </div>
    </div>
  );
}

export function DraggableAppendableField<T extends FieldValues>(
  props: DraggableAppendableFieldProps<T>
) {
  const {
    name,
    renderInput,
    renderRemoveButton,
    defaultAppendButtonText,
    renderAppendButton,
    appendButtonPosition = 'right',
    labels,
    rules,
    control,
    appendDefaultValues,
    maxItems,
  } = props;

  const {
    fields, append, remove, move,
  } = useFieldArray({ name, rules, control });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const disableAppend = !!maxItems && fields.length >= maxItems;
  const fieldIds = fields.map((field) => field.id);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = fieldIds.indexOf(active.id as string);
      const newIndex = fieldIds.indexOf(over.id as string);

      move(oldIndex, newIndex);
    }
  };

  const removeButton = (index: number) => (renderRemoveButton
    ? renderRemoveButton(() => remove(index))
    : (
      <button
        onClick={() => remove(index)}
        type="button"
        className="btn p-1 rounded-circle"
      >
        <img src="/static/img/icons/trash.svg" width={24} alt="Remover" />
      </button>
    ));

  const showLabels = labels && labels.length > 0 && fields.length > 0;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >

      {showLabels && <DraggableAppendableFieldLabels labels={labels} />}

      <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
        <div className="d-flex flex-column gap-3">
          {fields.map((field, index) => (
            <SortableItem
              id={field.id}
              key={field.id}
              renderInput={(isDragging) => renderInput(field.id, index, isDragging)}
              removeButton={removeButton(index)}
            />
          ))}
        </div>
      </SortableContext>

      <div className={clsx('d-flex', {
        'justify-content-end': appendButtonPosition === 'right',
        'justify-content-start': appendButtonPosition === 'left',
      })}
      >
        {renderAppendButton
          ? renderAppendButton(append as UseFieldArrayAppend<FieldValues, ArrayPath<T>>)
          : (
            <DefaultAppendButton
              disabled={disableAppend}
              handleAppend={() => {
                append(appendDefaultValues as FieldArray<T, ArrayPath<T>>);
              }}
              buttonText={defaultAppendButtonText}
            />
          )}
      </div>
    </DndContext>
  );
}
